import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import fi from 'date-fns/locale/fi';
import L from 'leaflet';
import { Spinner, Badge, Table, Modal } from 'react-bootstrap';
import {
  getKohoProjects,
  getKohoCustomers,
  getKohoInvoices,
  getKohoWorkSessions,
  getKohoEmployees,
  getKohoOffers,
  getKohoOffersByCustomer,
  getKohoCustomerCategories,
  getKohoProjectPlannedLoads,
  getKohoEmployeeFlexHours,
  getKohoEmployeeInvoicingBreakdown,
  getKohoShifts,
  getKohoExpenses,
  getKohoContracts,
  getKohoAssignments,
  searchKiinteistot,
  getKiinteistotWithRakennukset,
  getKohoCustomerById,
  getReportsByKiinteisto,
} from '../api/api';
import Resultdisplay from '../components/Resultsdisplay';
import { fetchCoverImageWithCache } from '../components/utils/fetchCoverImageWithCache';
import { BsBuildingFill } from 'react-icons/bs';

const BRAND = '#04aa00';

// Returns first responsible employee name(s) from responsibilities or participations
function resolveResponsible(item, employeesById) {
  const sources = [
    ...(item.responsibilities ?? []),
    ...(item.participations ?? []),
  ];
  const responsible = sources.filter(r => r.sales_role || r.responsible || r.technical_role);
  const ids = responsible.length ? responsible : sources; // fall back to all if none flagged
  const names = ids
    .map(r => employeesById.get(r.employee_id))
    .filter(Boolean);
  if (names.length === 0) return null;
  return names.join(', ');
}

function ResponsibleCell({ item, employeesById }) {
  const name = resolveResponsible(item, employeesById);
  return <td>{name ?? <span className="text-muted">—</span>}</td>;
}

function sortDesc(arr, key) {
  return [...arr].sort((a, b) => {
    const av = a[key] ?? '';
    const bv = b[key] ?? '';
    return bv < av ? -1 : bv > av ? 1 : 0;
  });
}

function useKoho(fetchFn, params, sortBy = 'created_at') {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchFn(params)
      .then(res => {
        const json = res.data;
        const arr = Array.isArray(json) ? json : (json.results ?? []);
        setData(sortBy ? sortDesc(arr, sortBy) : arr);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error };
}


const PAGE_SIZE = 100;

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  return (
    <div className="d-flex align-items-center gap-2 mt-2">
      <button className="btn btn-sm btn-outline-secondary py-0" disabled={page === 0} onClick={() => onChange(page - 1)}>‹</button>
      <span className="small text-muted">{page + 1} / {pages}</span>
      <button className="btn btn-sm btn-outline-secondary py-0" disabled={page >= pages - 1} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

function TablePane({ loading, error, children }) {
  if (loading) return (
    <div className="d-flex justify-content-center py-5">
      <Spinner animation="border" style={{ color: BRAND }} />
    </div>
  );
  if (error) return <div className="alert alert-danger mt-3">Virhe: {error}</div>;
  return children;
}

// ── Geocode via backend /api/haku/hae-kiinteistoja ───────────────────────
// Returns { lat, lng } or null. Results cached in sessionStorage.
// Returns { lat, lng, osoite, kaupunki } on success, or { osoite, kaupunki } (no coords) on failure
async function geocodeCustomer(customer) {
  const osoite = customer?.delivery_address || customer?.address || '';
  const kaupunki = customer?.delivery_city || customer?.city || '';
  if (!osoite && !kaupunki) return { osoite, kaupunki };

  const key = `geocode-backend:${osoite}|${kaupunki}`;
  const hit = sessionStorage.getItem(key);
  if (hit) return JSON.parse(hit);

  try {
    const res = await searchKiinteistot('', osoite, kaupunki);
    const coords = res.data?.[0]?.rakennukset?.[0]?.geometry?.coordinates;
    // GeoJSON order is [lng, lat]
    const result = coords
      ? { lat: coords[1], lng: coords[0], osoite, kaupunki }
      : { osoite, kaupunki };
    sessionStorage.setItem(key, JSON.stringify(result));
    return result;
  } catch {
    return { osoite, kaupunki };
  }
}

const MAP_PIN_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 24 24">
    <path fill="#007acc" stroke="white" stroke-width="0.75"
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
  </svg>`;

function ProjectLocationModal({ data, onHide }) {
  const project = data?.project ?? null;
  const customer = data?.customer ?? null;

  const mapElRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound
  const [usedAddress, setUsedAddress] = useState('');

  useEffect(() => {
    if (!project) return;
    let map = null;
    let cancelled = false;

    setStatus('loading');
    setUsedAddress('');

    geocodeCustomer(customer).then(result => {
      if (cancelled) return;
      const { lat, lng, osoite, kaupunki } = result ?? {};
      setUsedAddress([osoite, kaupunki].filter(Boolean).join(', '));
      if (!lat) { setStatus('notfound'); return; }

      const coords = { lat, lng };
      const container = mapElRef.current;
      if (!container) { setStatus('notfound'); return; }

      // Set explicit dimensions before L.map() — same pattern as MapVisualMultiple
      container.style.height = '420px';
      container.style.width = '100%';

      map = L.map(container, {
        center: [coords.lat, coords.lng],
        zoom: 16,
        scrollWheelZoom: true,
        zoomControl: false,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxNativeZoom: 17,
        crossOrigin: true,
      }).addTo(map);

      L.control.zoom({ position: 'topright' }).addTo(map);

      const icon = L.divIcon({
        html: MAP_PIN_SVG,
        className: '',
        iconSize: [40, 50],
        iconAnchor: [20, 50],
        popupAnchor: [0, -50],
      });

      L.marker([coords.lat, coords.lng], { icon })
        .bindPopup(`<strong>${project.customer_name}</strong>`)
        .addTo(map)
        .openPopup();

      // Fix tile offset caused by modal animation — same as MapVisualMultiple line 159
      setTimeout(() => map.invalidateSize(), 0);

      setStatus('ok');
    });

    return () => {
      cancelled = true;
      map?.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!project) return null;

  return (
    <Modal show onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <strong>{project.name}</strong>
          <span className="text-muted fw-normal ms-2">{project.customer_name}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ minHeight: 420 }}>
        {/* Map div is always in DOM so Leaflet can measure it — spinner overlays on top */}
        <div style={{ position: 'relative', height: 420 }}>
          <div ref={mapElRef} style={{ height: '100%', width: '100%', borderRadius: '0 0 4px 4px' }} />
          {status === 'loading' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', zIndex: 1000 }}>
              <Spinner animation="border" style={{ color: BRAND }} />
            </div>
          )}
          {status === 'notfound' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', zIndex: 1000, color: '#888', gap: 4 }}>
              <span>Sijaintia ei löydy</span>
              {usedAddress && <em style={{ fontSize: '0.85rem' }}>{usedAddress}</em>}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

// Fields shown in the main table — everything else goes in the info modal
const PROJECT_MAIN_FIELDS = new Set([
  'id', 'external_project_key', 'name', 'customer_id', 'customer_name',
  'active', 'start_date', 'end_date', 'templates',
]);

function ProjectInfoModal({ project, onHide }) {
  if (!project) return null;

  const extras = Object.entries(project).filter(([k]) => !PROJECT_MAIN_FIELDS.has(k));

  function renderValue(val) {
    if (val === null || val === undefined || val === '') return <span className="text-muted">—</span>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className="text-muted">[]</span>;
      return (
        <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }
    if (typeof val === 'object') return (
      <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(val, null, 2)}
      </pre>
    );
    if (typeof val === 'boolean') return <Badge bg={val ? 'success' : 'secondary'}>{val ? 'true' : 'false'}</Badge>;
    return String(val);
  }

  return (
    <Modal show onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          #{project.external_project_key} — {project.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Templates */}
        {project.templates?.length > 0 && (
          <div className="mb-3">
            <div className="fw-semibold small text-uppercase text-muted mb-1">Mallit</div>
            {project.templates.map(t => (
              <div key={t.id} className="border rounded p-2 mb-2 small">
                <div className="fw-semibold">{t.name}</div>
                {t.task_templates?.length > 0 && (
                  <ul className="mb-0 mt-1 ps-3">
                    {t.task_templates.map(tt => (
                      <li key={tt.id}>{tt.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {/* All other fields */}
        <div className="fw-semibold small text-uppercase text-muted mb-2">Muut kentät</div>
        <table className="table table-sm table-borderless mb-0">
          <tbody>
            {extras.map(([key, val]) => (
              <tr key={key}>
                <td className="text-muted small pe-3" style={{ whiteSpace: 'nowrap', width: 1 }}>{key}</td>
                <td className="small">{renderValue(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>
    </Modal>
  );
}

function ProjectsTab({ onCustomerClick, employeesById, customersById, onAddressSearch }) {
  const { data, loading, error } = useKoho(getKohoProjects, {}, 'created_at');
  const [infoProject, setInfoProject] = useState(null);
  const [mapProject, setMapProject] = useState(null);
  const [fetchingAddress, setFetchingAddress] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [categoryTab, setCategoryTab] = useState('taloyhtiö');

  const activeCat = CUSTOMER_CATEGORIES.find(c => c.key === categoryTab);
  const catFiltered = data.filter(p => getCustomerCategory(p.customer_name) === activeCat.prefix);
  const filtered = search.trim()
    ? catFiltered.filter(p => {
        const q = search.trim().toLowerCase();
        return p.name?.toLowerCase().includes(q) || p.customer_name?.toLowerCase().includes(q);
      })
    : catFiltered;
  const displayed = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const openAddressSearch = async (customerId) => {
    if (!customerId) return;
    let customer = customersById.get(String(customerId));
    if (!customer) {
      setFetchingAddress(customerId);
      try {
        const res = await getKohoCustomerById(customerId);
        customer = res.data;
      } catch { return; }
      finally { setFetchingAddress(null); }
    }
    const addr = customer.delivery_address || customer.address;
    if (!addr) return;
    onAddressSearch(addr, customer.delivery_city || customer.city || '');
  };

  return (
    <TablePane loading={loading} error={error}>
      <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
        {CUSTOMER_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            className={`btn btn-sm ${categoryTab === cat.key ? 'btn-success' : 'btn-outline-secondary'}`}
            onClick={() => { setCategoryTab(cat.key); setPage(0); }}
          >
            {cat.label}
          </button>
        ))}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: 240 }}
          placeholder="Hae nimellä tai asiakkaalla..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
        />
        {search && (
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setPage(0); }}>
            <i className="bi bi-x" />
          </button>
        )}
        <span className="text-muted small text-nowrap">
          {search.trim() ? `${filtered.length} tulosta` : `${catFiltered.length} projektia`}
        </span>
      </div>
      <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="align-middle koho-table">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Nimi</th>
              <th>Asiakas</th>
              <th>Vastuuhenkilö</th>
              <th>Mallit</th>
              <th>Tila</th>
              <th>Alkaa</th>
              <th>Päättyy</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(p => (
              <tr key={p.id}>
                <td className="text-muted">{p.external_project_key ?? '—'}</td>
                <td><strong>{p.name}</strong></td>
                <td>
                  {p.customer_id ? (
                    <button
                      className="btn btn-link p-0 text-start"
                      style={{ color: BRAND, textDecoration: 'underline dotted' }}
                      onClick={() => onCustomerClick(p.customer_id, p)}
                    >
                      {p.customer_name}
                    </button>
                  ) : (p.customer_name ?? '—')}
                </td>
                <ResponsibleCell item={p} employeesById={employeesById} />
                <td>
                  {p.templates?.length > 0 ? (
                    <div className="d-flex flex-column gap-1">
                      {p.templates.map(t => (
                        <div key={t.id}>
                          <span className="badge text-bg-light border">{t.name}</span>
                          {t.task_templates?.length > 0 && (
                            <div className="ms-1 mt-1 d-flex flex-wrap gap-1">
                              {t.task_templates.map(tt => (
                                <span key={tt.id} className="badge text-bg-secondary" style={{ fontWeight: 400 }}>
                                  {tt.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : <span className="text-muted">—</span>}
                </td>
                <td>
                  {p.active
                    ? <Badge bg="success">Aktiivinen</Badge>
                    : <Badge bg="secondary">Päättynyt</Badge>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{p.start_date ?? '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{p.end_date ?? '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div className="d-flex gap-1 flex-nowrap">
                  <button
                    className="btn btn-sm btn-outline-secondary py-1 px-2"
                    style={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                    onClick={() => setInfoProject(p)}
                    title="Lisätiedot"
                  >
                    i
                  </button>
                  {(() => { const c = customersById.get(String(p.customer_id)); return c && (c.delivery_address || c.address); })() && (
                    <button
                      className="btn btn-sm btn-outline-secondary py-1 px-2"
                      style={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                      onClick={() => setMapProject({ project: p, customer: customersById.get(String(p.customer_id)) })}
                      title="Näytä sijainti"
                    >
                      📍
                    </button>
                  )}
                  {p.customer_id && (
                    <button
                      className="btn btn-sm btn-outline-success py-1 px-2"
                      style={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                      title="Hae kiinteistöjä tällä osoitteella"
                      disabled={fetchingAddress === p.customer_id}
                      onClick={() => openAddressSearch(p.customer_id)}
                    >
                      {fetchingAddress === p.customer_id
                        ? <Spinner animation="border" size="sm" />
                        : <><i className="bi bi-house-search me-1" />Hae osoitteella</>}
                    </button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Pagination page={page} total={filtered.length} onChange={setPage} />
      <ProjectInfoModal project={infoProject} onHide={() => setInfoProject(null)} />
      <ProjectLocationModal data={mapProject} onHide={() => setMapProject(null)} />
    </TablePane>
  );
}

const CUSTOMER_MAIN_FIELDS = new Set([
  'id', 'name', 'organization_id', 'email', 'phone', 'city', 'archived',
  'persons', 'employees', 'responsibilities',
]);

let customersCache = null;

const CUSTOMER_CATEGORIES = [
  { key: 'taloyhtiö',       label: 'Taloyhtiöasiakkaat', prefix: null },
  { key: 'isännöinti',      label: 'Isännöintitoimistot', prefix: '1' },
  { key: 'yritys',          label: 'Yritysasiakkaat',     prefix: '2' },
  { key: 'julkinen',        label: 'Julkinen sektori',    prefix: '3' },
];

function getCustomerCategory(name = '') {
  const m = name.match(/^(\d+)\.\s/);
  return m ? m[1] : null;
}

function CustomerInfoModal({ customer, onHide }) {
  if (!customer) return null;

  const extras = Object.entries(customer).filter(([k]) => !CUSTOMER_MAIN_FIELDS.has(k));

  function renderValue(val) {
    if (val === null || val === undefined || val === '') return <span className="text-muted">—</span>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className="text-muted">[]</span>;
      return (
        <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }
    if (typeof val === 'object') return (
      <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(val, null, 2)}
      </pre>
    );
    if (typeof val === 'boolean') return <Badge bg={val ? 'success' : 'secondary'}>{val ? 'true' : 'false'}</Badge>;
    return String(val);
  }

  const activePerson = customer.persons?.filter(p => !p.archived) ?? [];
  const activeEmployees = customer.employees?.filter(e => e.active) ?? [];

  return (
    <Modal show onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">{customer.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {activePerson.length > 0 && (
          <div className="mb-3">
            <div className="fw-semibold small text-uppercase text-muted mb-1">Yhteyshenkilöt</div>
            {activePerson.map((p, i) => (
              <div key={p.id ?? i} className="border rounded p-2 mb-2 small">
                <div className="fw-semibold">{[p.first_name, p.last_name].filter(Boolean).join(' ') || '—'}</div>
                {p.position && <div className="text-muted">{p.position}</div>}
                {p.email && <div>{p.email}</div>}
                {p.phone && <div>{p.phone}</div>}
              </div>
            ))}
          </div>
        )}

        {activeEmployees.length > 0 && (
          <div className="mb-3">
            <div className="fw-semibold small text-uppercase text-muted mb-1">Vastuuhenkilöt</div>
            {activeEmployees.map((e, i) => (
              <div key={e.id ?? i} className="border rounded p-2 mb-2 small">
                <div className="fw-semibold">{e.name || '—'}</div>
                {e.title && <div className="text-muted">{e.title}</div>}
                {e.email && <div>{e.email}</div>}
              </div>
            ))}
          </div>
        )}

        <div className="fw-semibold small text-uppercase text-muted mb-2">Muut kentät</div>
        <table className="table table-sm table-borderless mb-0">
          <tbody>
            {extras.map(([key, val]) => (
              <tr key={key}>
                <td className="text-muted small pe-3" style={{ whiteSpace: 'nowrap', width: 1 }}>{key}</td>
                <td className="small">{renderValue(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>
    </Modal>
  );
}

function KorttiWithReports({ k }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);

  useEffect(() => {
    getReportsByKiinteisto(k.id_kiinteisto)
      .then(res => setReports(Array.isArray(res.data) ? res.data : []))
      .catch(() => setReports([]));
  }, [k.id_kiinteisto]);

  const openReport = (r) => {
    if (r.id_kiinteisto) {
      navigate(`/taloyhtiokortti/${r.id_kiinteisto}/raportti/${r.type}/${r.id}/${r?.report_sections?.[0]?.type}/${r?.report_sections?.[0]?.id}`);
    } else {
      navigate(`/taloyhtiokortti/raportti/${r.type}/${r.id}/${r?.report_sections?.[0]?.type}/${r?.report_sections?.[0]?.id}`);
    }
  };

  return (
    <div className="border-0 rounded-4 overflow-hidden bg-white" style={{ width: 453, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
      {/* Kortti header */}
      <Link to={`/taloyhtiokortti/${k.id_kiinteisto}`} className="text-decoration-none">
        <div
          style={{
            height: 160,
            backgroundColor: k.coverImageUrl ? 'transparent' : BRAND,
            backgroundImage: k.coverImageUrl ? `url(${k.coverImageUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {k.coverImageUrl
            ? <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)' }} />
            : <BsBuildingFill size={36} color="white" />}
        </div>
        <div className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
          <div className="fw-semibold text-dark text-truncate">{k.kohteen_nimi || k.rakennukset_fulls?.[0]?.osoite || '—'}</div>
          <div className="text-muted text-truncate">{k.rakennukset_fulls?.[0]?.osoite}</div>
          <div className="text-muted">{[k.rakennukset_fulls?.[0]?.postinumero, k.rakennukset_fulls?.[0]?.toimipaikka].filter(Boolean).join(' ')}</div>
        </div>
      </Link>

      {/* Reports — visually nested inside the same card */}
      {reports.length > 0 && (
        <div style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #e9ecef', padding: '8px' }}>
          <div className="text-muted mb-2" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Raportit ({reports.length})
          </div>
          <div className="d-flex flex-column gap-2">
            {reports.map(r => {
              const msPerDay = 24 * 60 * 60 * 1000;
              const daysSince = r.field_date ? Math.floor((Date.now() - new Date(r.field_date).getTime()) / msPerDay) : null;
              const daysLeft = daysSince !== null ? 30 - daysSince : null;
              const fieldDateBg = daysLeft === null ? 'rgba(0,0,0,0.5)'
                : daysLeft > 14 ? 'rgba(25,135,84,0.85)'
                : daysLeft > 7  ? 'rgba(255,193,7,0.85)'
                : daysLeft >= 0 ? 'rgba(220,53,69,0.85)'
                : 'rgba(33,37,41,0.85)';
              const fieldDateColor = (daysLeft !== null && daysLeft > 7 && daysLeft <= 14) ? '#000' : '#fff';
              return (
                <div key={r.id} className="rounded-3 overflow-hidden bg-white" style={{ border: '1px solid #dee2e6', fontSize: '0.82rem' }}>
                  <div className="position-relative" style={{ height: 80 }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: k.coverImageUrl ? 'transparent' : '#e9ecef',
                      backgroundImage: k.coverImageUrl ? `url(${k.coverImageUrl})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }} />
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 8px',
                    }}>
                      <span style={{
                        color: '#fff', fontWeight: 600, fontSize: '0.82rem',
                        textAlign: 'center', textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                        lineHeight: 1.3,
                      }}>
                        {r.name || r.type}
                      </span>
                    </div>
                    {r.field_date && (
                      <div className="position-absolute top-0 end-0 m-1 px-1 rounded"
                        style={{ backgroundColor: fieldDateBg, color: fieldDateColor, fontSize: '0.65rem', lineHeight: 1.4 }}>
                        <div>Kenttäpäivä: {format(new Date(r.field_date), 'd.M.yyyy', { locale: fi })}</div>
                        {daysLeft !== null && (
                          <div style={{ opacity: 0.9 }}>
                            {daysLeft > 0 ? `${daysLeft} pv jäljellä` : `Ylitetty ${Math.abs(daysLeft)} pv`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="p-2 d-flex flex-column gap-1">
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Päivitetty: {r.updated_at ? format(new Date(r.updated_at), 'd.M.yyyy HH:mm', { locale: fi }) : '–'}
                    </div>
                    <button className="btn btn-outline-success btn-sm w-100 mt-1" onClick={() => openReport(r)}>
                      Avaa raportti
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectPlannedLoads({ projectId, employeesById }) {
  const [open, setOpen] = useState(false);
  const [loads, setLoads] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = () => {
    setOpen(o => !o);
    if (loads === null) {
      setLoading(true);
      getKohoProjectPlannedLoads(projectId)
        .then(res => setLoads(Array.isArray(res.data) ? res.data : (res.data.results ?? res.data.planned_loads ?? [])))
        .catch(() => setLoads([]))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
      <button
        className="btn btn-sm btn-outline-secondary py-0"
        style={{ fontSize: '0.78rem' }}
        onClick={toggle}
      >
        <i className={`bi bi-chevron-${open ? 'up' : 'down'} me-1`} />
        Kuormitus
      </button>
      {open && (
        <div className="mt-2">
          {loading && <Spinner animation="border" size="sm" />}
          {!loading && loads?.length === 0 && <p className="text-muted small mb-0">Ei kuormitusdataa.</p>}
          {!loading && loads?.length > 0 && (
            <Table size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  {Object.keys(loads[0]).map(k => <th key={k} style={{ fontSize: '0.75rem' }}>{k}</th>)}
                </tr>
              </thead>
              <tbody>
                {loads.map((row, i) => (
                  <tr key={i}>
                    {Object.entries(row).map(([k, v]) => (
                      <td key={k} style={{ fontSize: '0.78rem' }}>
                        {k === 'employee_id' && employeesById?.get(v)
                          ? employeesById.get(v)
                          : v == null ? '—' : String(v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}

function CustomersTab({ selectedCustomerId, selectedProject, selectedContract, onSelectCustomer, onClearSelection, employeesById, onAddressSearch, onCategoryClick }) {
  const [data, setData] = useState(customersCache ?? []);
  const [loading, setLoading] = useState(!customersCache);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [infoCustomer, setInfoCustomer] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [categoryTab, setCategoryTab] = useState('taloyhtiö');

  useEffect(() => {
    if (customersCache && refresh === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getKohoCustomers(refresh > 0 ? { refresh: true } : {})
      .then(res => {
        const batch = Array.isArray(res.data) ? res.data : (res.data.results ?? []);
        const sorted = sortDesc(batch, 'created_at');
        customersCache = sorted;
        if (!cancelled) setData(sorted);
      })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh]);


  const selectedCustomer = selectedCustomerId ? data.find(c => c.id === selectedCustomerId) ?? null : null;

  const [kortit, setKortit] = useState([]);
  const [kortitLoading, setKortitLoading] = useState(false);
  const [customerDetailTab, setCustomerDetailTab] = useState('projects');

  const [customerProjects, setCustomerProjects] = useState([]);
  const [customerProjectsLoading, setCustomerProjectsLoading] = useState(false);
  const [customerContracts, setCustomerContracts] = useState([]);
  const [customerContractsLoading, setCustomerContractsLoading] = useState(false);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [customerInvoicesLoading, setCustomerInvoicesLoading] = useState(false);
  const [customerOffers, setCustomerOffers] = useState([]);
  const [customerOffersLoading, setCustomerOffersLoading] = useState(false);

  useEffect(() => {
    if (!selectedCustomerId) {
      setCustomerProjects([]); setCustomerContracts([]); setCustomerInvoices([]); setCustomerOffers([]); setCustomerDetailTab('projects');
      return;
    }
    setCustomerProjectsLoading(true);
    getKohoProjects({ customer_id: selectedCustomerId, archived: undefined })
      .then(res => setCustomerProjects(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .catch(() => setCustomerProjects([]))
      .finally(() => setCustomerProjectsLoading(false));

    setCustomerContractsLoading(true);
    getKohoContracts({ customer_id: selectedCustomerId, archived: undefined })
      .then(res => setCustomerContracts(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .catch(() => setCustomerContracts([]))
      .finally(() => setCustomerContractsLoading(false));

    setCustomerInvoicesLoading(true);
    getKohoInvoices({ customer_id: selectedCustomerId })
      .then(res => setCustomerInvoices(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .catch(() => setCustomerInvoices([]))
      .finally(() => setCustomerInvoicesLoading(false));

    setCustomerOffersLoading(true);
    getKohoOffersByCustomer(selectedCustomerId)
      .then(res => setCustomerOffers(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .catch(() => setCustomerOffers([]))
      .finally(() => setCustomerOffersLoading(false));
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedCustomer) { setKortit([]); return; }
    const addr = selectedCustomer.delivery_address || selectedCustomer.address || '';
    if (!addr) { setKortit([]); return; }
    setKortitLoading(true);
    getKiinteistotWithRakennukset(1, 20, 'id_kiinteisto', 'ASC', addr)
      .then(async res => {
        const items = (res.data?.data ?? []).map(k => ({ ...k, coverImageUrl: null }));
        setKortit(items);
        items.forEach(async k => {
          const url = await fetchCoverImageWithCache(k.id_kiinteisto);
          if (url) setKortit(prev => prev.map(x => x.id_kiinteisto === k.id_kiinteisto ? { ...x, coverImageUrl: url } : x));
        });
      })
      .catch(() => setKortit([]))
      .finally(() => setKortitLoading(false));
  }, [selectedCustomer]);

  const activeCat = CUSTOMER_CATEGORIES.find(c => c.key === categoryTab);
  const baseList = data.filter(c =>
    getCustomerCategory(c.name) === activeCat.prefix
  );
  const filtered = selectedCustomerId
    ? (selectedCustomer ? [selectedCustomer] : [])
    : search.trim()
      ? baseList.filter(c => {
          const q = search.trim().toLowerCase();
          return c.name?.toLowerCase().includes(q) ||
            c.delivery_address?.toLowerCase().includes(q) ||
            c.address?.toLowerCase().includes(q) ||
            c.delivery_city?.toLowerCase().includes(q) ||
            c.city?.toLowerCase().includes(q);
        })
      : baseList;
  const displayed = selectedCustomerId ? filtered : filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <TablePane
      loading={loading}
      error={error}
    >
      <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
        {CUSTOMER_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            className={`btn btn-sm ${categoryTab === cat.key ? 'btn-success' : 'btn-outline-secondary'}`}
            onClick={() => { setCategoryTab(cat.key); setPage(0); onClearSelection(); }}
          >
            {cat.label}
          </button>
        ))}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        {!selectedCustomerId && (
          <div className="d-flex gap-1">
            <input
              type="search"
              className="form-control form-control-sm"
              style={{ maxWidth: 240 }}
              placeholder="Hae nimellä tai osoitteella..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
            />
            {search && (
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setPage(0); }}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>
        )}
        <p className="text-muted small mb-0 text-nowrap">
          {selectedCustomerId
            ? `${filtered.length} asiakas`
            : search.trim()
              ? `${filtered.length} tulosta`
              : `${baseList.length} asiakasta`}
        </p>
        {!selectedCustomerId && (
          <button
            className="btn btn-sm btn-outline-secondary ms-auto"
            onClick={() => { customersCache = null; setRefresh(r => r + 1); setPage(0); }}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Päivitä asiakkaat'}
          </button>
        )}
        {selectedCustomerId && (
          <button
            className="btn btn-sm btn-outline-secondary py-0"
            onClick={onClearSelection}
          >
            Näytä kaikki
          </button>
        )}
      </div>
      {selectedProject && (
        <div className="border rounded p-3 mb-2 bg-light d-flex flex-wrap align-items-start gap-3">
          <div>
            <div className="text-muted" style={{ fontSize: '0.786rem' }}>Projekti</div>
            <div className="fw-semibold" style={{ fontSize: '1rem' }}>#{selectedProject.external_project_key} {selectedProject.name}</div>
          </div>
          {selectedProject.templates?.length > 0 && (
            <div>
              <div className="text-muted" style={{ fontSize: '0.786rem' }}>Tyyppi</div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {selectedProject.templates.map(t => (
                  <span key={t.id} className="badge text-bg-secondary fw-normal" style={{ fontSize: '0.786rem' }}>{t.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {selectedContract && (
        <div className="border rounded p-3 mb-2 bg-light" style={{ fontSize: '0.86rem' }}>
          {/* Header row */}
          <div className="d-flex flex-wrap align-items-start gap-3 mb-2">
            <div>
              <div className="text-muted" style={{ fontSize: '0.786rem' }}>Sopimus</div>
              <div className="fw-semibold" style={{ fontSize: '1rem' }}>{selectedContract.name || `#${selectedContract.id}`}</div>
            </div>
            {selectedContract.start_date && (
              <div>
                <div className="text-muted" style={{ fontSize: '0.786rem' }}>Alkaa</div>
                <div>{selectedContract.start_date}</div>
              </div>
            )}
            {selectedContract.end_date && (
              <div>
                <div className="text-muted" style={{ fontSize: '0.786rem' }}>Päättyy</div>
                <div>{selectedContract.end_date}</div>
              </div>
            )}
            {selectedContract.billing_interval && (
              <div>
                <div className="text-muted" style={{ fontSize: '0.786rem' }}>Laskutusväli</div>
                <div>{selectedContract.billing_interval}</div>
              </div>
            )}
            {selectedContract.one_time_net_total != null && (
              <div>
                <div className="text-muted" style={{ fontSize: '0.786rem' }}>Summa</div>
                <div>{selectedContract.one_time_net_total.toLocaleString('fi-FI')} €</div>
              </div>
            )}
          </div>

          {/* Products */}
          {selectedContract.products?.length > 0 && (
            <div className="mb-2">
              <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Tuotteet</div>
              <div className="d-flex flex-column gap-1">
                {selectedContract.products.map((p, i) => (
                  <div key={p.id ?? i} className="border rounded px-2 py-1 bg-white">
                    <div className="d-flex justify-content-between align-items-baseline gap-2">
                      <span className="fw-semibold">{p.name || '—'}</span>
                      {p.price != null && (
                        <span className="text-nowrap">{p.price.toLocaleString('fi-FI')} €{p.monthly ? '/kk' : ''}</span>
                      )}
                    </div>
                    {p.description && <div className="text-muted" style={{ fontSize: '0.786rem' }}>{p.description}</div>}
                    <div className="d-flex gap-3 text-muted" style={{ fontSize: '0.786rem' }}>
                      {p.count != null && <span>{p.count} kpl</span>}
                      {p.vat != null && <span>alv {p.vat}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contract-level description */}
          {selectedContract.description && (
            <div>
              <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Kuvaus</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{selectedContract.description}</div>
            </div>
          )}
        </div>
      )}
      <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="align-middle koho-table">
          <thead className="table-light">
            <tr>
              <th>Nimi</th>
              <th>Y-tunnus</th>
              <th>Kategoria</th>
              {/* <th>Sähköposti</th>
              <th>Puhelin</th> */}
              <th>Toimitusosoite</th>
              <th>Kaupunki</th>
              <th>Vastuuhenkilö</th>
              <th>Tila</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(c => (
              <tr
                key={c.id}
                style={{ cursor: 'pointer', ...(c.id === selectedCustomerId ? { background: '#f0faf0' } : {}) }}
                onClick={() => onSelectCustomer?.(c.id)}
              >
                <td><strong>{c.name}</strong></td>
                <td className="text-muted">{c.organization_id || '—'}</td>
                <td onClick={e => { if (c.customer_category_id) { e.stopPropagation(); onCategoryClick?.(c.customer_category_id); } }}>
                  {c.customer_category_id
                    ? <span style={{ color: '#0d6efd', cursor: 'pointer' }}>{c.category_name || `#${c.customer_category_id}`}</span>
                    : <span className="text-muted">—</span>}
                </td>
                {/* <td>{c.email || '—'}</td>
                <td>{c.phone || '—'}</td> */}
                <td>
                  {(c.delivery_address || c.address) ?? '—'}
                </td>
                <td>
                  {(c.delivery_city || c.city) ?? '—'}
                </td>
                <ResponsibleCell item={c} employeesById={employeesById} />
                <td>
                  {c.archived
                    ? <Badge bg="secondary">Arkistoitu</Badge>
                    : <Badge bg="success">Aktiivinen</Badge>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div className="d-flex gap-1 flex-nowrap">
                    <button
                      className="btn btn-sm btn-outline-secondary py-1 px-2"
                      style={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                      onClick={() => setInfoCustomer(c)}
                      title="Lisätiedot"
                    >
                      i
                    </button>
                    {(c.delivery_address || c.address) && (
                      <button
                        className="btn btn-sm btn-outline-success py-1 px-2"
                        style={{ fontSize: '0.85rem', lineHeight: 1.5, whiteSpace: 'nowrap' }}
                        title="Hae kiinteistöjä tällä osoitteella"
                        onClick={() => onAddressSearch(c.delivery_address || c.address, c.delivery_city || c.city || '')}
                      >
                        <i className="bi bi-house-search me-1" />Hae osoitteella
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {!selectedCustomerId && <Pagination page={page} total={filtered.length} onChange={setPage} />}
      {selectedCustomerId && selectedCustomer && (
        <>
        <div className="mt-3 d-flex gap-3 align-items-stretch flex-wrap flex-md-nowrap">
          {/* Left: invoice info — flex: 1 (narrower golden ratio part) */}
          <div className="koho-section-card card-billing d-flex flex-column" style={{ flex: 1, minWidth: 220 }}>
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-receipt me-1" />Laskutustiedot
            </div>
            <div className="d-flex flex-column justify-content-between flex-grow-1" style={{ fontSize: '0.86rem' }}>
              {[
                ['Laskutusnimi',      selectedCustomer.invoice_name],
                ['Y-tunnus',          selectedCustomer.organization_id],
                ['ALV-tunnus',        selectedCustomer.vat_id],
                ['Puhelin',           selectedCustomer.phone],
                ['Sähköposti',        selectedCustomer.billing_email || selectedCustomer.email],
                ['Laskutusosoite',    [selectedCustomer.address, selectedCustomer.zip_code, selectedCustomer.city].filter(Boolean).join(', ')],
                ['Toimitusosoite',    [selectedCustomer.delivery_address, selectedCustomer.delivery_zip_code, selectedCustomer.delivery_city].filter(Boolean).join(', ')],
                ['OVT-tunnus',        selectedCustomer.ovt_identifier],
                ['Välittäjätunnus',   selectedCustomer.electronic_invoicing_intermediary],
                ['Laskutustapa',      selectedCustomer.default_invoice_delivery_method],
                ['Verkkolaskuosoite', selectedCustomer.web_invoice_address],
                ['Maksuehto',         selectedCustomer.default_payment_term != null ? `${selectedCustomer.default_payment_term} pv` : null],
                ['Valuutta',          selectedCustomer.currency],
                ['Nollaverollinen',   selectedCustomer.zero_vat ? 'Kyllä' : null],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label}>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
                  <div className="fw-semibold" title={value}>{value}</div>
                </div>
              ))}
              {selectedCustomer.customer_category_id && (
                <div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>Kategoria</div>
                  <button
                    className="btn btn-link p-0 fw-semibold"
                    style={{ fontSize: 'inherit' }}
                    onClick={() => onCategoryClick?.(selectedCustomer.customer_category_id)}
                  >
                    {selectedCustomer.category_name || `#${selectedCustomer.customer_category_id}`}
                  </button>
                </div>
              )}
            </div>
            {selectedCustomer.persons?.filter(p => p.first_name || p.last_name).length > 0 && (
              <div className="mt-3 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                <div className="text-muted mb-2" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Yhteyshenkilöt</div>
                <div className="d-flex flex-column gap-2">
                  {selectedCustomer.persons.filter(p => p.first_name || p.last_name).map(p => (
                    <div key={p.id} className="border rounded px-2 py-1" style={{ fontSize: '0.8rem' }}>
                      <div className="fw-semibold">{[p.first_name, p.last_name].filter(Boolean).join(' ')}</div>
                      {p.position && <div className="text-muted">{p.position}</div>}
                      {p.email && <div className="text-muted">{p.email}</div>}
                      {p.phone && <div className="text-muted">{p.phone}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: taloyhtiökortit + reports — flex: 1.618 (wider golden ratio part) */}
          <div className="p-3" style={{ flex: 1.618, minWidth: 0, border: '1px solid transparent' }}>
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-buildings me-1" />Taloyhtiökortit samalla osoitteella
            </div>
            {kortitLoading && <Spinner animation="border" size="sm" />}
            {!kortitLoading && kortit.length === 0 && (
              <p className="text-muted small mb-0">Ei löytynyt taloyhtiökortteja tälle osoitteelle.</p>
            )}
            {!kortitLoading && kortit.length > 0 && (
              <div className="d-flex flex-wrap gap-3">
                {kortit.map(k => (
                  <KorttiWithReports key={k.id_kiinteisto} k={k} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Projects, contracts, invoices for selected customer */}
        <div className="mt-3">
          <ul className="nav nav-tabs mb-3">
            {[
              { key: 'projects',  label: 'Projektit & Sopimukset', icon: 'bi-kanban', loading: customerProjectsLoading || customerContractsLoading, count: customerProjects.length + customerContracts.length },
              { key: 'offers',    label: 'Tarjoukset', icon: 'bi-file-earmark-check', loading: customerOffersLoading,   count: customerOffers.length },
              { key: 'invoices',  label: 'Laskut',     icon: 'bi-receipt',           loading: customerInvoicesLoading,  count: customerInvoices.length },
            ].map(t => (
              <li key={t.key} className="nav-item">
                <button
                  className={`nav-link${customerDetailTab === t.key ? ' active' : ''}`}
                  onClick={() => setCustomerDetailTab(t.key)}
                >
                  <i className={`bi ${t.icon} me-1`} />
                  {t.label}
                  {t.loading
                    ? <Spinner animation="border" size="sm" className="ms-1" />
                    : t.count > 0 && <span className="badge bg-secondary ms-1 fw-normal">{t.count}</span>}
                </button>
              </li>
            ))}
          </ul>

          <div className={customerDetailTab === 'projects' ? '' : 'd-none'}>
            <div className="koho-section-card card-projects">
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-kanban me-1" />Projektit
              {customerProjectsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!customerProjectsLoading && customerProjects.length === 0 && <p className="text-muted small mb-0">Ei projekteja.</p>}
            <div className="d-flex flex-column gap-2">
              {customerProjects.map(p => (
                <div key={p.id} className="border rounded p-3 bg-light" style={{ fontSize: '0.86rem' }}>
                  {/* Header */}
                  <div className="d-flex flex-wrap align-items-start gap-3 mb-2">
                    <div>
                      <div className="text-muted" style={{ fontSize: '0.786rem' }}>
                        Projekti{p.external_project_key ? ` #${p.external_project_key}` : ''}
                      </div>
                      <div className="fw-semibold" style={{ fontSize: '1rem' }}>
                        {p.name}
                        <span className="ms-2">
                          {p.active === false
                            ? <Badge bg="secondary">Ei aktiivinen</Badge>
                            : <Badge bg="success">Aktiivinen</Badge>}
                        </span>
                      </div>
                    </div>
                    {p.phase && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Vaihe</div>
                        <div>{p.phase}</div>
                      </div>
                    )}
                    {p.start_date && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Alkaa</div>
                        <div>{p.start_date}</div>
                      </div>
                    )}
                    {p.end_date && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Päättyy</div>
                        <div>{p.end_date}</div>
                      </div>
                    )}
                    {p.created_at && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Luotu</div>
                        <div>{p.created_at.slice(0, 10)}</div>
                      </div>
                    )}
                  </div>

                  {/* Financials */}
                  {(p.price_per_hour != null || p.budget_total != null || p.net_total_budget != null || p.invoicable_budget != null || p.hours_budget != null || p.contract_hours != null || p.trip_kms) && (
                    <div className="d-flex flex-wrap gap-3 mb-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      {p.price_per_hour != null && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Tuntihinta</div>
                          <div className="koho-money">{p.price_per_hour.toLocaleString('fi-FI')} €/h</div>
                        </div>
                      )}
                      {p.budget_total != null && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Budjetti</div>
                          <div className="koho-money">{p.budget_total.toLocaleString('fi-FI')} €</div>
                        </div>
                      )}
                      {p.net_total_budget != null && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Nettobudjetti</div>
                          <div className="koho-money">{p.net_total_budget.toLocaleString('fi-FI')} €</div>
                        </div>
                      )}
                      {p.invoicable_budget != null && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Laskutettava budjetti</div>
                          <div className="koho-money">{p.invoicable_budget.toLocaleString('fi-FI')} €</div>
                        </div>
                      )}
                      {p.hours_budget != null && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Tuntikiintiö</div>
                          <div>{p.hours_budget} h</div>
                        </div>
                      )}
                      {p.contract_hours != null && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Sopimustunnit</div>
                          <div>{p.contract_hours} h</div>
                        </div>
                      )}
                      {p.trip_kms != null && p.trip_kms !== 0 && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Matkat</div>
                          <div>{p.trip_kms} km</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* References */}
                  {(p.our_reference || p.your_reference) && (
                    <div className="d-flex gap-3 mb-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      {p.our_reference && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Viitteemme</div>
                          <div>{p.our_reference}</div>
                        </div>
                      )}
                      {p.your_reference && (
                        <div>
                          <div className="text-muted" style={{ fontSize: '0.786rem' }}>Viitteenne</div>
                          <div>{p.your_reference}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Participants & workload */}
                  {p.participations?.length > 0 && (() => {
                    const linkedContract = p.contract_id
                      ? customerContracts.find(c => c.id === p.contract_id)
                      : null;
                    const total = linkedContract?.one_time_net_total ?? null;
                    const perPerson = total != null && p.participations.length > 0
                      ? total / p.participations.length
                      : null;
                    return (
                      <div className="mb-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                          Osallistujat
                          {perPerson != null && (
                            <span className="ms-2 fw-normal text-muted" style={{ textTransform: 'none' }}>
                              (sopimus {total.toLocaleString('fi-FI')} € ÷ {p.participations.length} = {perPerson.toLocaleString('fi-FI', { maximumFractionDigits: 2 })} € / hlö)
                            </span>
                          )}
                        </div>
                        <Table size="sm" className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ fontSize: '0.75rem' }}>Henkilö</th>
                              <th style={{ fontSize: '0.75rem' }}>Kuormitus</th>
                              {perPerson != null && <th style={{ fontSize: '0.75rem' }}>Osuus</th>}
                              <th style={{ fontSize: '0.75rem' }}>Alkaa</th>
                              <th style={{ fontSize: '0.75rem' }}>Päättyy</th>
                              <th style={{ fontSize: '0.75rem' }}>Rooli</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.participations.map(part => {
                              const name = employeesById.get(part.employee_id) ?? `#${part.employee_id}`;
                              return (
                                <tr key={part.id}>
                                  <td style={{ fontSize: '0.78rem' }}><strong>{name}</strong></td>
                                  <td style={{ fontSize: '0.78rem' }}>
                                    {part.load != null ? `${part.load} h` : '—'}
                                  </td>
                                  {perPerson != null && (
                                    <td style={{ fontSize: '0.78rem' }} className="text-success fw-semibold">
                                      {perPerson.toLocaleString('fi-FI', { maximumFractionDigits: 2 })} €
                                    </td>
                                  )}
                                  <td style={{ fontSize: '0.78rem' }} className="text-muted">{part.start ?? '—'}</td>
                                  <td style={{ fontSize: '0.78rem' }} className="text-muted">{part.end ?? '—'}</td>
                                  <td style={{ fontSize: '0.78rem' }}>
                                    {part.responsible && <Badge bg="success" className="fw-normal me-1">Vastuuhenkilö</Badge>}
                                    {part.sales_role && <Badge bg="info" className="fw-normal">Myynti</Badge>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    );
                  })()}

                  {/* Description */}
                  {p.description && (
                    <div className="pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Kuvaus</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{p.description}</div>
                    </div>
                  )}

                </div>
              ))}
            </div>
          </div>

          </div>{/* end projects section */}
          <div className={customerDetailTab === 'projects' ? '' : 'd-none'}>
            <div className="koho-section-card card-contracts">
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-file-earmark-text me-1" />Sopimukset
              {customerContractsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!customerContractsLoading && customerContracts.length === 0 && <p className="text-muted small mb-0">Ei sopimuksia.</p>}
            <div className="d-flex flex-column gap-2">
              {customerContracts.map(c => (
                <div key={c.id} className="border rounded p-3 bg-light" style={{ fontSize: '0.86rem' }}>
                  <div className="d-flex flex-wrap align-items-start gap-3 mb-2">
                    <div>
                      <div className="text-muted" style={{ fontSize: '0.786rem' }}>Sopimus</div>
                      <div className="fw-semibold" style={{ fontSize: '1rem' }}>
                        {c.name || `#${c.id}`}
                        {c.archived && <Badge bg="secondary" className="ms-2 fw-normal">Arkistoitu</Badge>}
                      </div>
                    </div>
                    {c.start_date && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Alkaa</div>
                        <div>{c.start_date}</div>
                      </div>
                    )}
                    {c.end_date && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Päättyy</div>
                        <div>{c.end_date}</div>
                      </div>
                    )}
                    {c.billing_interval && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Laskutusväli</div>
                        <div>{c.billing_interval}</div>
                      </div>
                    )}
                    {c.one_time_net_total != null && (
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.786rem' }}>Summa</div>
                        <div className="koho-money">{c.one_time_net_total.toLocaleString('fi-FI')} €</div>
                      </div>
                    )}
                  </div>
                  {c.products?.length > 0 && (
                    <div className="mb-2">
                      <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Tuotteet</div>
                      <div className="d-flex flex-column gap-1">
                        {c.products.map((p, i) => (
                          <div key={p.id ?? i} className="border rounded px-2 py-1 bg-white">
                            <div className="d-flex justify-content-between align-items-baseline gap-2">
                              <span className="fw-semibold">{p.name || '—'}</span>
                              {p.price != null && (
                                <span className="text-nowrap koho-money">{p.price.toLocaleString('fi-FI')} €{p.monthly ? '/kk' : ''}</span>
                              )}
                            </div>
                            {p.description && <div className="text-muted" style={{ fontSize: '0.786rem' }}>{p.description}</div>}
                            <div className="d-flex gap-3 text-muted" style={{ fontSize: '0.786rem' }}>
                              {p.count != null && <span>{p.count} kpl</span>}
                              {p.vat != null && <span>alv {p.vat}%</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {c.description && (
                    <div>
                      <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Kuvaus</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{c.description}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          </div>{/* end projects & contracts tab */}
          <div className={customerDetailTab === 'offers' ? '' : 'd-none'}>
            <div className="koho-section-card card-offers">
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-file-earmark-check me-1" />Tarjoukset
              {customerOffersLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!customerOffersLoading && customerOffers.length === 0 && <p className="text-muted small mb-0">Ei tarjouksia.</p>}
            <div className="d-flex flex-column gap-2">
              {[...customerOffers].sort((a, b) => {
                const rank = o => o.outcome === 'accepted' ? 0 : o.outcome === 'won' ? 1 : 2;
                if (rank(a) !== rank(b)) return rank(a) - rank(b);
                return (b.originated_at ?? '') > (a.originated_at ?? '') ? 1 : -1;
              }).map(o => (
                <div key={o.id} className="border rounded p-3 bg-light" style={{ fontSize: '0.86rem' }}>
                  {/* Header: name + outcome */}
                  <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    <div className="fw-semibold" style={{ fontSize: '1rem' }}>
                      {o.name || `#${o.id}`}
                      {o.is_lead && <Badge bg="info" className="ms-2 fw-normal">Liidi</Badge>}
                    </div>
                    {offerOutcomeBadge(o.outcome)}
                  </div>

                  {/* Two-column details: dates left, sums right */}
                  <div className="d-flex gap-4 flex-wrap">
                    <table className="table table-sm table-borderless mb-0" style={{ width: 'auto' }}>
                      <tbody>
                        {o.probability != null && (
                          <tr>
                            <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Todennäköisyys</td>
                            <td className="py-0">{o.probability} %</td>
                          </tr>
                        )}
                        {o.originated_at && (
                          <tr>
                            <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Luotu</td>
                            <td className="py-0">{o.originated_at.slice(0, 10)}</td>
                          </tr>
                        )}
                        {o.valid_to && (
                          <tr>
                            <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Voimassa</td>
                            <td className="py-0">{o.valid_to}</td>
                          </tr>
                        )}
                        {o.resolved_at && (
                          <tr>
                            <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Ratkaistu</td>
                            <td className="py-0">{o.resolved_at.slice(0, 10)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {(o.one_time_net_total != null || (o.monthly_net_total != null && o.monthly_net_total !== 0) || (o.estimated_product_total != null && o.estimated_product_total !== 0) || (o.estimated_service_total != null && o.estimated_service_total !== 0)) && (
                      <table className="table table-sm table-borderless mb-0" style={{ width: 'auto' }}>
                        <tbody>
                          {o.one_time_net_total != null && (
                            <tr>
                              <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Kertasumma</td>
                              <td className="py-0 text-end koho-money">{o.one_time_net_total.toLocaleString('fi-FI')} €</td>
                            </tr>
                          )}
                          {o.monthly_net_total != null && o.monthly_net_total !== 0 && (
                            <tr>
                              <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Kuukausittain</td>
                              <td className="py-0 text-end koho-money">{o.monthly_net_total.toLocaleString('fi-FI')} €/kk</td>
                            </tr>
                          )}
                          {o.estimated_product_total != null && o.estimated_product_total !== 0 && (
                            <tr>
                              <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Tuotearvio</td>
                              <td className="py-0 text-end koho-money">{o.estimated_product_total.toLocaleString('fi-FI')} €</td>
                            </tr>
                          )}
                          {o.estimated_service_total != null && o.estimated_service_total !== 0 && (
                            <tr>
                              <td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Palveluarvio</td>
                              <td className="py-0 text-end koho-money">{o.estimated_service_total.toLocaleString('fi-FI')} €</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {o.description && (
                    <div className="mt-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Kuvaus</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{o.description}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          </div>{/* end offers tab */}
          <div className={customerDetailTab === 'invoices' ? '' : 'd-none'}>
          {/* Invoices */}
          <div className="koho-section-card card-invoices">
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-receipt me-1" />Laskut
              {customerInvoicesLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!customerInvoicesLoading && customerInvoices.length === 0 && <p className="text-muted small mb-0">Ei laskuja.</p>}
            {customerInvoices.length > 0 && (
              <Table hover size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Nimi</th>
                    <th>Laskupäivä</th>
                    <th>Eräpäivä</th>
                    <th className="text-end">Netto (€)</th>
                    <th>Tila</th>
                  </tr>
                </thead>
                <tbody>
                  {customerInvoices.map(inv => (
                    <tr key={inv.id}>
                      <td>{inv.name ?? `Lasku #${inv.id}`}</td>
                      <td className="text-muted">{inv.billing_date ?? '—'}</td>
                      <td className="text-muted">{inv.due_date ?? '—'}</td>
                      <td className="text-end koho-money">{inv.net_amount != null ? inv.net_amount.toLocaleString('fi-FI') : '—'}</td>
                      <td>
                        {inv.paid
                          ? <Badge bg="success">Maksettu</Badge>
                          : inv.sent
                            ? <Badge bg="warning" text="dark">Lähetetty</Badge>
                            : <Badge bg="secondary">Luonnos</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
          </div>{/* end invoices tab */}

        </div>{/* end tab container */}
        </>
      )}
      <CustomerInfoModal customer={infoCustomer} onHide={() => setInfoCustomer(null)} />
    </TablePane>
  );
}

function InvoicesTab() {
  const year = new Date().getFullYear();
  const { data, loading, error } = useKoho(getKohoInvoices, { start_date: `${year}-01-01` }, 'billing_date');

  function statusBadge(inv) {
    if (inv.paid) return <Badge bg="success">Maksettu</Badge>;
    if (inv.sent) return <Badge bg="warning" text="dark">Lähetetty</Badge>;
    return <Badge bg="secondary">Luonnos</Badge>;
  }

  return (
    <TablePane loading={loading} error={error}>
      <p className="text-muted small mb-2">{data.length} laskua ({year})</p>
      <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="align-middle koho-table">
          <thead className="table-light">
            <tr>
              <th>Nimi</th>
              <th>Laskupäivä</th>
              <th>Eräpäivä</th>
              <th className="text-end">Netto (€)</th>
              <th className="text-end">Brutto (€)</th>
              <th>Tila</th>
            </tr>
          </thead>
          <tbody>
            {data.map(inv => (
              <tr key={inv.id}>
                <td>{inv.name ?? `Lasku #${inv.id}`}</td>
                <td>{inv.billing_date ?? '—'}</td>
                <td>{inv.due_date ?? '—'}</td>
                <td className="text-end">{inv.net_amount != null ? inv.net_amount.toLocaleString('fi-FI') : '—'}</td>
                <td className="text-end">{inv.gross_amount != null ? inv.gross_amount.toLocaleString('fi-FI') : '—'}</td>
                <td>{statusBadge(inv)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </TablePane>
  );
}

function WorkSessionsTab() {
  const firstOfMonth = new Date();
  firstOfMonth.setDate(1);
  const startDate = firstOfMonth.toISOString().slice(0, 10);
  const { data, loading, error } = useKoho(getKohoWorkSessions, { start_date: startDate }, 'date');

  const totalHours = data.reduce((sum, s) => sum + (s.hours ?? 0), 0);

  return (
    <TablePane loading={loading} error={error}>
      <p className="text-muted small mb-2">
        {data.length} kirjausta alkaen {startDate} &mdash; yhteensä{' '}
        <strong>{totalHours.toLocaleString('fi-FI', { maximumFractionDigits: 1 })} h</strong>
      </p>
      <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="align-middle koho-table">
          <thead className="table-light">
            <tr>
              <th>Päivä</th>
              <th className="text-end">Tunnit</th>
              <th className="text-end">Lask. tunnit</th>
              <th>Laskutustapa</th>
              <th>Vahvistettu</th>
            </tr>
          </thead>
          <tbody>
            {data.map(s => (
              <tr key={s.id}>
                <td>{s.date}</td>
                <td className="text-end">{s.hours ?? '—'}</td>
                <td className="text-end">{s.invoice_hours ?? '—'}</td>
                <td>{s.invoicing_type ?? '—'}</td>
                <td>
                  {s.confirmed
                    ? <Badge bg="success">Kyllä</Badge>
                    : <Badge bg="secondary">Ei</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </TablePane>
  );
}

function EmpLazySection({ title, icon, fetchFn, employeeKey, render }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); setOpen(false); }, [employeeKey]);

  const toggle = () => {
    setOpen(o => !o);
    if (data === null) {
      setLoading(true);
      fetchFn()
        .then(res => {
          const raw = res.data;
          const arr = Array.isArray(raw) ? raw : (raw?.results ?? raw?.data ?? []);
          setData(arr);
        })
        .catch(() => setData([]))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="border rounded-3 bg-white p-3">
      <button
        className="btn btn-sm btn-outline-secondary py-0 d-flex align-items-center gap-2"
        style={{ fontSize: '0.78rem' }}
        onClick={toggle}
      >
        <i className={`bi ${icon}`} />
        {title}
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} />
        {loading && <Spinner animation="border" size="sm" />}
      </button>
      {open && !loading && data !== null && (
        <div className="mt-2" style={{ overflowX: 'auto' }}>
          {data.length === 0
            ? <p className="text-muted small mb-0">Ei dataa.</p>
            : render(data)}
        </div>
      )}
    </div>
  );
}

function EmployeesTab({ onCustomerClick }) {
  const { data, loading, error } = useKoho(getKohoEmployees, undefined, 'created_at');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('employee') ? Number(searchParams.get('employee')) : null;

  const setSelectedId = (id) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (id != null) next.set('employee', id); else next.delete('employee');
    return next;
  });

  const selected = selectedId ? data.find(e => e.id === selectedId) ?? null : null;

  const [empProjects, setEmpProjects] = useState([]);
  const [empProjectsLoading, setEmpProjectsLoading] = useState(false);
  const [empContracts, setEmpContracts] = useState([]);
  const [empSessions, setEmpSessions] = useState([]);
  const [empSessionsLoading, setEmpSessionsLoading] = useState(false);
  const [empAssignments, setEmpAssignments] = useState([]);
  const [empAssignmentsLoading, setEmpAssignmentsLoading] = useState(false);

  useEffect(() => {
    if (!selectedId) {
      setEmpProjects([]); setEmpContracts([]); setEmpSessions([]); setEmpAssignments([]);
      return;
    }
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().slice(0, 10);

    setEmpProjectsLoading(true);
    getKohoProjects({ archived: undefined })
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : (res.data.results ?? []);
        const filtered = all.filter(p => p.participations?.some(part => part.employee_id === selectedId));
        setEmpProjects(filtered);
        // Fetch contracts for these projects
        const contractIds = [...new Set(filtered.map(p => p.contract_id).filter(Boolean))];
        if (contractIds.length > 0) {
          getKohoContracts({ archived: undefined })
            .then(cr => {
              const all = Array.isArray(cr.data) ? cr.data : (cr.data.results ?? []);
              setEmpContracts(all.filter(c => contractIds.includes(c.id)));
            })
            .catch(() => {});
        }
      })
      .catch(() => setEmpProjects([]))
      .finally(() => setEmpProjectsLoading(false));

    setEmpSessionsLoading(true);
    getKohoWorkSessions({ employee_id: selectedId, start_date: start })
      .then(res => setEmpSessions(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .catch(() => setEmpSessions([]))
      .finally(() => setEmpSessionsLoading(false));

    setEmpAssignmentsLoading(true);
    getKohoAssignments({ employee_id: selectedId })
      .then(res => setEmpAssignments(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .catch(() => setEmpAssignments([]))
      .finally(() => setEmpAssignmentsLoading(false));
  }, [selectedId]);

  const displayed = selectedId ? (selected ? [selected] : []) : data;

  return (
    <TablePane loading={loading} error={error}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <p className="text-muted small mb-0">{data.length} työntekijää</p>
        {selectedId && (
          <button className="btn btn-sm btn-outline-secondary py-0 ms-auto" onClick={() => setSelectedId(null)}>
            Näytä kaikki
          </button>
        )}
      </div>
      <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="align-middle koho-table">
          <thead className="table-light">
            <tr>
              <th>Nimi</th>
              <th>Titteli</th>
              <th>Sähköposti</th>
              <th>Tila</th>
              <th className="text-end">Tuntikulut (€)</th>
              <th className="text-end">Tuntihinta (€)</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(e => (
              <tr
                key={e.id}
                style={{ cursor: 'pointer', ...(e.id === selectedId ? { background: '#f0faf0' } : {}) }}
                onClick={() => setSelectedId(e.id === selectedId ? null : e.id)}

              >
                <td><strong>{e.name}</strong></td>
                <td>{e.title ?? '—'}</td>
                <td>{e.email ?? '—'}</td>
                <td>
                  {e.active
                    ? <Badge bg="success">Aktiivinen</Badge>
                    : <Badge bg="secondary">Ei aktiivinen</Badge>}
                </td>
                <td className="text-end">{e.hourly_cost ?? '—'}</td>
                <td className="text-end">{e.default_price_per_hour ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {selected && (
        <div className="d-flex flex-column gap-3 mt-3">

          {/* Employee info */}
          <div className="border rounded-3 bg-white p-3" style={{ fontSize: '0.86rem' }}>
            <div className="text-muted mb-2" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              <i className="bi bi-person me-1" />{selected.name}
            </div>
            <div className="d-flex flex-wrap gap-3">
              {[
                ['Titteli', selected.title],
                ['Sähköposti', selected.email],
                ['Puhelin', selected.phone],
                ['Tuntikulut', selected.hourly_cost != null ? `${selected.hourly_cost} €/h` : null],
                ['Tuntihinta', selected.default_price_per_hour != null ? `${selected.default_price_per_hour} €/h` : null],
                ['Luotu', selected.created_at?.slice(0, 10)],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label}>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
                  <div className="fw-semibold">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Projects */}
          <div className="border rounded-3 bg-white p-3">
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-kanban me-1" />Projektit
              {empProjectsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!empProjectsLoading && empProjects.length === 0 && <p className="text-muted small mb-0">Ei projekteja.</p>}
            <div style={{ overflowX: 'auto' }}>
              <Table hover size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Nimi</th>
                    <th>Asiakas</th>
                    <th>Alkaa</th>
                    <th>Päättyy</th>
                    <th>Luotu</th>
                    <th className="text-end">Osuus</th>
                    <th>Tila</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sorted = [...empProjects].sort((a, b) =>
                      (b.start_date ?? b.created_at ?? '') > (a.start_date ?? a.created_at ?? '') ? 1 : -1
                    );
                    // Group by YYYY-MM of start_date
                    const groups = [];
                    sorted.forEach(p => {
                      const key = (p.start_date ?? p.created_at ?? '').slice(0, 7) || 'tuntematon';
                      let g = groups.find(g => g.key === key);
                      if (!g) { g = { key, rows: [] }; groups.push(g); }
                      const contract = p.contract_id ? empContracts.find(c => c.id === p.contract_id) : null;
                      const total = contract?.one_time_net_total ?? null;
                      const count = p.participations?.length || 1;
                      const share = total != null ? total / count : null;
                      g.rows.push({ p, share });
                    });

                    return groups.flatMap(({ key, rows }) => {
                      const monthTotal = rows.reduce((s, { share }) => share != null ? s + share : s, 0);
                      const hasAny = rows.some(({ share }) => share != null);
                      const label = key !== 'tuntematon'
                        ? (() => { const s = new Date(key + '-01').toLocaleString('fi-FI', { month: 'long', year: 'numeric' }); return s.charAt(0).toUpperCase() + s.slice(1); })()
                        : 'Ei päivämäärää';
                      return [
                        <tr key={`hdr-${key}`}>
                          <td colSpan={8} className="bg-light fw-bold" style={{ fontSize: '1rem', paddingTop: '0.75rem', paddingBottom: '0.25rem', borderTop: '2px solid #dee2e6' }}>
                            {label}
                          </td>
                        </tr>,
                        ...rows.map(({ p, share }) => (
                          <tr
                            key={p.id}
                            style={{ cursor: p.customer_id ? 'pointer' : 'default' }}
                            onClick={() => p.customer_id && onCustomerClick?.(p.customer_id, p)}
                          >
                            <td className="text-muted">{p.external_project_key || p.id}</td>
                            <td><strong>{p.name}</strong>{p.description && <div className="text-muted" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{p.description}</div>}</td>
                            <td style={{ color: p.customer_id ? '#0d6efd' : undefined }}>{p.customer_name ?? '—'}</td>
                            <td className="text-muted">{p.start_date ?? '—'}</td>
                            <td className="text-muted">{p.end_date ?? '—'}</td>
                            <td className="text-muted">{p.created_at?.slice(0, 10) ?? '—'}</td>
                            <td className="text-end text-success fw-semibold">
                              {share != null ? `${share.toLocaleString('fi-FI', { maximumFractionDigits: 2 })} €` : '—'}
                            </td>
                            <td>
                              {p.active === false
                                ? <Badge bg="secondary">Ei aktiivinen</Badge>
                                : <Badge bg="success">Aktiivinen</Badge>}
                            </td>
                          </tr>
                        )),
                        <tr key={`total-${key}`} className="table-light">
                          <td colSpan={6} className="text-end text-muted fw-semibold" style={{ fontSize: '0.9rem' }}>
                            {label} yhteensä
                          </td>
                          <td className="text-end fw-bold text-success" style={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>
                            {hasAny ? `${monthTotal.toLocaleString('fi-FI', { maximumFractionDigits: 2 })} €` : '—'}
                          </td>
                          <td />
                        </tr>,
                      ];
                    });
                  })()}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Work sessions */}
          <div className="border rounded-3 bg-white p-3">
            <div className="text-muted mb-2" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              <i className="bi bi-clock-history me-1" />Tuntikirjaukset (2 kk)
              {empSessionsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!empSessionsLoading && empSessions.length === 0 && <p className="text-muted small mb-0">Ei tuntikirjauksia.</p>}
            {empSessions.length > 0 && (
              <Table hover size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr><th>Päivä</th><th>Projekti</th><th>Tehtävä</th><th className="text-end">Tunnit</th></tr>
                </thead>
                <tbody>
                  {empSessions.map(s => (
                    <tr key={s.id}>
                      <td className="text-muted">{s.date ?? '—'}</td>
                      <td>{s.project_name ?? '—'}</td>
                      <td className="text-muted">{s.task_name ?? '—'}</td>
                      <td className="text-end">{s.hours ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Assignments */}
          <div className="border rounded-3 bg-white p-3">
            <div className="text-muted mb-2" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              <i className="bi bi-clipboard-check me-1" />Työmääräykset
              {empAssignmentsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!empAssignmentsLoading && empAssignments.length === 0 && <p className="text-muted small mb-0">Ei työmääräyksiä.</p>}
            {empAssignments.length > 0 && (
              <Table hover size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr><th>Nimi</th><th>Asiakas</th><th>Päivä</th><th>Tila</th></tr>
                </thead>
                <tbody>
                  {empAssignments.map(a => (
                    <tr key={a.id}>
                      <td><strong>{a.name ?? `#${a.id}`}</strong></td>
                      <td className="text-muted">{a.customer_name ?? '—'}</td>
                      <td className="text-muted">{a.date ?? '—'}</td>
                      <td>
                        {a.completed
                          ? <Badge bg="success">Valmis</Badge>
                          : <Badge bg="warning" text="dark">Kesken</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Flex hours */}
          <EmpLazySection
            title="Liukumatiedot" icon="bi-clock"
            fetchFn={() => getKohoEmployeeFlexHours(selectedId)}
            employeeKey={selectedId}
            render={rows => (
              <Table size="sm" className="align-middle mb-0">
                <thead className="table-light"><tr>{Object.keys(rows[0]).map(k => <th key={k} style={{ fontSize: '0.75rem' }}>{k}</th>)}</tr></thead>
                <tbody>{rows.map((r, i) => <tr key={i}>{Object.values(r).map((v, j) => <td key={j} style={{ fontSize: '0.78rem' }}>{v == null ? '—' : String(v)}</td>)}</tr>)}</tbody>
              </Table>
            )}
          />

          {/* Invoicing breakdown */}
          {(() => {
            const now = new Date();
            const termStart = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
            const termEnd = now.toISOString().slice(0, 10);
            return (
              <EmpLazySection
                title={`Laskutuserittely (${now.getFullYear()})`} icon="bi-graph-up"
                fetchFn={() => getKohoEmployeeInvoicingBreakdown(selectedId, termStart, termEnd)}
                employeeKey={selectedId}
                render={rows => (
                  <Table size="sm" className="align-middle mb-0">
                    <thead className="table-light"><tr>{Object.keys(rows[0]).map(k => <th key={k} style={{ fontSize: '0.75rem' }}>{k}</th>)}</tr></thead>
                    <tbody>{rows.map((r, i) => <tr key={i}>{Object.values(r).map((v, j) => <td key={j} style={{ fontSize: '0.78rem' }}>{v == null ? '—' : String(v)}</td>)}</tr>)}</tbody>
                  </Table>
                )}
              />
            );
          })()}

          {/* Shifts */}
          <EmpLazySection
            title="Työvuorot" icon="bi-calendar-week"
            fetchFn={() => getKohoShifts({ 'employee_id[]': selectedId, start_date: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().slice(0, 10) })}
            employeeKey={selectedId}
            render={rows => (
              <Table size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr><th>Päivä</th><th>Alku</th><th>Loppu</th><th className="text-end">Tunnit</th></tr>
                </thead>
                <tbody>
                  {rows.map(s => (
                    <tr key={s.id}>
                      <td className="text-muted">{s.date ?? '—'}</td>
                      <td className="text-muted">{s.start_time ?? '—'}</td>
                      <td className="text-muted">{s.end_time ?? '—'}</td>
                      <td className="text-end">{s.hours ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          />

          {/* Expenses */}
          <EmpLazySection
            title="Kulut" icon="bi-receipt-cutoff"
            fetchFn={() => getKohoExpenses({ 'employee_id[]': selectedId })}
            employeeKey={selectedId}
            render={rows => (
              <Table size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr><th>Päivä</th><th>Kuvaus</th><th className="text-end">Summa (€)</th></tr>
                </thead>
                <tbody>
                  {rows.map(e => (
                    <tr key={e.id}>
                      <td className="text-muted">{e.date ?? '—'}</td>
                      <td>{e.description ?? e.name ?? '—'}</td>
                      <td className="text-end">{e.amount != null ? e.amount.toLocaleString('fi-FI') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          />

        </div>
      )}
    </TablePane>
  );
}

const OFFER_MAIN_FIELDS = new Set([
  'id', 'name', 'customer_id', 'customer', 'probability',
  'one_time_net_total', 'monthly_net_total', 'valid_to', 'outcome',
  'is_lead', 'products',
]);

const OFFER_OUTCOMES = {
  '':         { bg: '#1a73e8', label: 'Avoin' },
  'won':      { bg: '#04aa00', label: 'Voitettu' },
  'accepted': { bg: '#0d9488', label: 'Hyväksytty' },
  'lost':     { bg: '#dc2626', label: 'Hävitty' },
};

function offerOutcomeBadge(outcome) {
  const key = outcome ?? '';
  const match = OFFER_OUTCOMES[key];
  const { bg, label } = match ?? { bg: '#6b7280', label: outcome };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: '0.78rem',
      fontWeight: 600,
      color: '#fff',
      background: bg,
      letterSpacing: '0.01em',
    }}>
      {label}
    </span>
  );
}

function OfferInfoModal({ offer, onHide }) {
  if (!offer) return null;

  const extras = Object.entries(offer).filter(([k]) => !OFFER_MAIN_FIELDS.has(k));

  function renderValue(val) {
    if (val === null || val === undefined || val === '') return <span className="text-muted">—</span>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className="text-muted">[]</span>;
      return (
        <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }
    if (typeof val === 'object') return (
      <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(val, null, 2)}
      </pre>
    );
    if (typeof val === 'boolean') return <Badge bg={val ? 'success' : 'secondary'}>{val ? 'true' : 'false'}</Badge>;
    return String(val);
  }

  return (
    <Modal show onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">{offer.name || `Tarjous #${offer.id}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {offer.products?.length > 0 && (
          <div className="mb-3">
            <div className="fw-semibold small text-uppercase text-muted mb-1">Tuotteet</div>
            {offer.products.map((p, i) => (
              <div key={p.id ?? i} className="border rounded p-2 mb-2 small">
                <div className="d-flex justify-content-between">
                  <span className="fw-semibold">{p.name || '—'}</span>
                  {p.price != null && (
                    <span>{p.price.toLocaleString('fi-FI')} €{p.monthly ? '/kk' : ''}</span>
                  )}
                </div>
                {p.description && <div className="text-muted">{p.description}</div>}
                <div className="mt-1 d-flex gap-3 text-muted">
                  {p.count != null && <span>kpl: {p.count}</span>}
                  {p.vat != null && <span>alv: {p.vat}%</span>}
                  {p.discount_percentage != null && <span>alennus: {p.discount_percentage}%</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {offer.customer && (
          <div className="mb-3">
            <div className="fw-semibold small text-uppercase text-muted mb-1">Asiakas</div>
            <div className="border rounded p-2 small">
              <div className="fw-semibold">{offer.customer.name}</div>
              {offer.customer.organization_id && <div className="text-muted">{offer.customer.organization_id}</div>}
              {offer.customer.email && <div>{offer.customer.email}</div>}
              {offer.customer.phone && <div>{offer.customer.phone}</div>}
              {offer.customer.city && <div>{offer.customer.city}</div>}
            </div>
          </div>
        )}

        <div className="fw-semibold small text-uppercase text-muted mb-2">Muut kentät</div>
        <table className="table table-sm table-borderless mb-0">
          <tbody>
            {extras.map(([key, val]) => (
              <tr key={key}>
                <td className="text-muted small pe-3" style={{ whiteSpace: 'nowrap', width: 1 }}>{key}</td>
                <td className="small">{renderValue(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>
    </Modal>
  );
}

function OffersTab({ onCustomerClick, employeesById }) {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const fmt = (d) => d.toISOString().slice(0, 19) + '+03:00';

  const { data, loading, error } = useKoho(getKohoOffers, {
    created_after: fmt(firstOfMonth),
    created_before: fmt(lastOfMonth),
    updated_after: fmt(firstOfMonth),
    updated_before: fmt(lastOfMonth),
  }, 'created_at');

  const [infoOffer, setInfoOffer] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [categoryTab, setCategoryTab] = useState('taloyhtiö');

  const activeCat = CUSTOMER_CATEGORIES.find(c => c.key === categoryTab);
  const catFiltered = data.filter(o => getCustomerCategory(o.customer?.name) === activeCat.prefix);
  const filtered = search.trim()
    ? catFiltered.filter(o => {
        const q = search.trim().toLowerCase();
        return o.name?.toLowerCase().includes(q) || o.customer?.name?.toLowerCase().includes(q);
      })
    : catFiltered;
  const displayed = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <TablePane loading={loading} error={error}>
      <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
        {CUSTOMER_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            className={`btn btn-sm ${categoryTab === cat.key ? 'btn-success' : 'btn-outline-secondary'}`}
            onClick={() => { setCategoryTab(cat.key); setPage(0); }}
          >
            {cat.label}
          </button>
        ))}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: 240 }}
          placeholder="Hae nimellä tai asiakkaalla..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
        />
        {search && (
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setPage(0); }}>
            <i className="bi bi-x" />
          </button>
        )}
        <span className="text-muted small text-nowrap">
          {search.trim() ? `${filtered.length} tulosta` : `${catFiltered.length} tarjousta`}
        </span>
      </div>
      <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="align-middle koho-table">
          <thead className="table-light">
            <tr>
              <th>Nimi</th>
              <th>Asiakas</th>
              <th>Tila</th>
              <th>Liidi</th>
              <th className="text-end">Kertasumma (€)</th>
              <th className="text-end">Kuukausisumma (€)</th>
              <th>Vastuuhenkilö</th>
              <th>Voimassa</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(o => (
              <tr key={o.id}>
                <td><strong>{o.name || `#${o.id}`}</strong></td>
                <td>
                  {o.customer_id ? (
                    <button
                      className="btn btn-link p-0 text-start"
                      style={{ color: BRAND, textDecoration: 'underline dotted' }}
                      onClick={() => onCustomerClick(o.customer_id, o.customer?.name)}
                    >
                      {o.customer?.name ?? o.customer_id}
                    </button>
                  ) : (o.customer?.name ?? '—')}
                </td>
                <td>{offerOutcomeBadge(o.outcome)}</td>
                <td>{o.is_lead ? <Badge bg="info" text="dark">Liidi</Badge> : '—'}</td>
                <td className="text-end">
                  {o.one_time_net_total != null ? o.one_time_net_total.toLocaleString('fi-FI') : '—'}
                </td>
                <td className="text-end">
                  {o.monthly_net_total != null ? o.monthly_net_total.toLocaleString('fi-FI') : '—'}
                </td>
                <ResponsibleCell item={o} employeesById={employeesById} />
                <td>{o.valid_to ?? '—'}</td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-secondary py-1 px-2"
                    style={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                    onClick={() => setInfoOffer(o)}
                    title="Lisätiedot"
                  >
                    i
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Pagination page={page} total={filtered.length} onChange={setPage} />
      <OfferInfoModal offer={infoOffer} onHide={() => setInfoOffer(null)} />
    </TablePane>
  );
}

const CONTRACT_MAIN_FIELDS = new Set([
  'id', 'name', 'customer_id', 'start_date', 'end_date',
  'billing_interval', 'billing_mode', 'one_time_net_total',
  'confirmed_by_manager', 'products',
]);

function ContractInfoModal({ contract, onHide }) {
  if (!contract) return null;

  const extras = Object.entries(contract).filter(([k]) => !CONTRACT_MAIN_FIELDS.has(k));

  function renderValue(val) {
    if (val === null || val === undefined || val === '') return <span className="text-muted">—</span>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className="text-muted">[]</span>;
      return (
        <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }
    if (typeof val === 'object') return (
      <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(val, null, 2)}
      </pre>
    );
    if (typeof val === 'boolean') return <Badge bg={val ? 'success' : 'secondary'}>{val ? 'true' : 'false'}</Badge>;
    return String(val);
  }

  return (
    <Modal show onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">{contract.name || `Sopimus #${contract.id}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {contract.products?.length > 0 && (
          <div className="mb-3">
            <div className="fw-semibold small text-uppercase text-muted mb-1">Tuotteet</div>
            {contract.products.map((p, i) => (
              <div key={p.id ?? i} className="border rounded p-2 mb-2 small">
                <div className="d-flex justify-content-between">
                  <span className="fw-semibold">{p.name || '—'}</span>
                  {p.price != null && (
                    <span>{p.price.toLocaleString('fi-FI')} €{p.monthly ? '/kk' : ''}</span>
                  )}
                </div>
                {p.description && <div className="text-muted">{p.description}</div>}
                <div className="mt-1 d-flex gap-3 text-muted">
                  {p.count != null && <span>kpl: {p.count}</span>}
                  {p.vat != null && <span>alv: {p.vat}%</span>}
                  {p.term_start && <span>{p.term_start} – {p.term_end ?? '?'}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="fw-semibold small text-uppercase text-muted mb-2">Muut kentät</div>
        <table className="table table-sm table-borderless mb-0">
          <tbody>
            {extras.map(([key, val]) => (
              <tr key={key}>
                <td className="text-muted small pe-3" style={{ whiteSpace: 'nowrap', width: 1 }}>{key}</td>
                <td className="small">{renderValue(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>
    </Modal>
  );
}

function ContractsTab({ onCustomerClick, employeesById, customersById }) {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const fmt = (d) => d.toISOString().slice(0, 19) + '+03:00';

  const { data, loading, error } = useKoho(getKohoContracts, {
    created_after: fmt(firstOfMonth),
    created_before: fmt(lastOfMonth),
    updated_after: fmt(firstOfMonth),
    updated_before: fmt(lastOfMonth),
  }, 'created_at');

  const [infoContract, setInfoContract] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [categoryTab, setCategoryTab] = useState('taloyhtiö');

  const activeCat = CUSTOMER_CATEGORIES.find(c => c.key === categoryTab);
  const catFiltered = data.filter(c => getCustomerCategory(customersById?.get(String(c.customer_id))?.name) === activeCat.prefix);
  const filtered = search.trim()
    ? catFiltered.filter(c => {
        const q = search.trim().toLowerCase();
        const customerName = customersById?.get(String(c.customer_id))?.name ?? '';
        return c.name?.toLowerCase().includes(q) || customerName.toLowerCase().includes(q);
      })
    : catFiltered;
  const displayed = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <TablePane loading={loading} error={error}>
      <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
        {CUSTOMER_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            className={`btn btn-sm ${categoryTab === cat.key ? 'btn-success' : 'btn-outline-secondary'}`}
            onClick={() => { setCategoryTab(cat.key); setPage(0); }}
          >
            {cat.label}
          </button>
        ))}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: 240 }}
          placeholder="Hae nimellä tai asiakkaalla..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
        />
        {search && (
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setPage(0); }}>
            <i className="bi bi-x" />
          </button>
        )}
        <span className="text-muted small text-nowrap">
          {search.trim() ? `${filtered.length} tulosta` : `${catFiltered.length} sopimusta`}
        </span>
      </div>
      <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="align-middle koho-table">
          <thead className="table-light">
            <tr>
              <th>Nimi</th>
              <th>Asiakas</th>
              <th>Alkaa</th>
              <th>Päättyy</th>
              <th>Laskutusväli</th>
              <th>Laskutustapa</th>
              <th className="text-end">Kertasumma (€)</th>
              <th>Vastuuhenkilö</th>
              <th>Vahvistettu</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(c => (
              <tr key={c.id}>
                <td><strong>{c.name || `#${c.id}`}</strong></td>
                <td>
                  {c.customer_id ? (
                    <button
                      className="btn btn-link p-0 text-start"
                      style={{ color: BRAND, textDecoration: 'underline dotted' }}
                      onClick={() => onCustomerClick(c.customer_id, null, c)}
                    >
                      {customersById?.get(String(c.customer_id))?.name || c.customer_id}
                    </button>
                  ) : '—'}
                </td>
                <td>{c.start_date ?? '—'}</td>
                <td>{c.end_date ?? '—'}</td>
                <td>{c.billing_interval ?? '—'}</td>
                <td>{c.billing_mode || '—'}</td>
                <td className="text-end">
                  {c.one_time_net_total != null ? c.one_time_net_total.toLocaleString('fi-FI') : '—'}
                </td>
                <ResponsibleCell item={c} employeesById={employeesById} />
                <td>
                  {c.confirmed_by_manager
                    ? <Badge bg="success">Kyllä</Badge>
                    : <Badge bg="secondary">Ei</Badge>}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-secondary py-1 px-2"
                    style={{ fontSize: '0.85rem', lineHeight: 1.5 }}
                    onClick={() => setInfoContract(c)}
                    title="Lisätiedot"
                  >
                    i
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Pagination page={page} total={filtered.length} onChange={setPage} />
      <ContractInfoModal contract={infoContract} onHide={() => setInfoContract(null)} />
    </TablePane>
  );
}

function CategoriesTab({ customersData, onCustomerClick, employeesById }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategoryId = searchParams.get('category') ? Number(searchParams.get('category')) : null;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getKohoCustomerCategories()
      .then(res => setCategories(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const selectCategory = (id) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (id != null) next.set('category', id); else next.delete('category');
    return next;
  });

  const selectedCategory = selectedCategoryId ? categories.find(c => c.id === selectedCategoryId) : null;
  const categoryCustomers = selectedCategoryId
    ? customersData.filter(c => c.customer_category_id === selectedCategoryId)
    : [];
  const categoryCustomerIds = new Set(categoryCustomers.map(c => c.id));

  const [catOffers, setCatOffers] = useState([]);
  const [catOffersLoading, setCatOffersLoading] = useState(false);

  useEffect(() => {
    if (!selectedCategoryId || categoryCustomers.length === 0) { setCatOffers([]); return; }
    setCatOffersLoading(true);
    getKohoOffers({ limit: 500 })
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : (res.data.results ?? []);
        setCatOffers(all.filter(o => categoryCustomerIds.has(o.customer_id)));
      })
      .catch(() => setCatOffers([]))
      .finally(() => setCatOffersLoading(false));
  }, [selectedCategoryId]);

  return (
    <TablePane loading={loading} error={error}>
      <div className="d-flex align-items-center gap-2 mb-3">
        {selectedCategory && (
          <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => selectCategory(null)}>
            ← Kaikki kategoriat
          </button>
        )}
      </div>

      {!selectedCategory && (
        <div className="d-flex flex-wrap gap-3">
          {categories.map(cat => {
            const count = customersData.filter(c => c.customer_category_id === cat.id).length;
            return (
              <div
                key={cat.id}
                className="border rounded-3 bg-white p-3"
                style={{ minWidth: 220, cursor: 'pointer', flex: '1 1 220px' }}
                onClick={() => selectCategory(cat.id)}
              >
                <div className="fw-semibold" style={{ fontSize: '1rem' }}>{cat.name}</div>
                {cat.description && <div className="text-muted small mt-1">{cat.description}</div>}
                <div className="mt-2">
                  <Badge bg="secondary" className="fw-normal">{count} asiakasta</Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedCategory && (
        <div>
          <h5 className="mb-3">{selectedCategory.name}
            <span className="ms-2 text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
              — {categoryCustomers.length} asiakasta
            </span>
          </h5>
          {selectedCategory.description && (
            <p className="text-muted mb-3">{selectedCategory.description}</p>
          )}
          <div className="koho-table-wrap" style={{ overflowX: 'auto' }}>
            <Table hover size="sm" className="align-middle koho-table">
              <thead className="table-light">
                <tr>
                  <th>Nimi</th>
                  <th>Y-tunnus</th>
                  <th>Toimitusosoite</th>
                  <th>Kaupunki</th>
                  <th>Vastuuhenkilö</th>
                  <th>Tila</th>
                </tr>
              </thead>
              <tbody>
                {categoryCustomers.map(c => (
                  <tr
                    key={c.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCustomerClick(c.id)}
                  >
                    <td><strong>{c.name}</strong></td>
                    <td className="text-muted">{c.organization_id || '—'}</td>
                    <td>{c.delivery_address || c.address || '—'}</td>
                    <td>{c.delivery_city || c.city || '—'}</td>
                    <ResponsibleCell item={c} employeesById={employeesById} />
                    <td>
                      {c.archived
                        ? <Badge bg="secondary">Arkistoitu</Badge>
                        : <Badge bg="success">Aktiivinen</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Offers for this category */}
          <div className="border rounded-3 bg-white p-3 mt-3">
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.95rem' }}>
              <i className="bi bi-file-earmark-check me-1" />Tarjoukset
              {catOffersLoading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
            {!catOffersLoading && catOffers.length === 0 && <p className="text-muted small mb-0">Ei tarjouksia.</p>}
            {catOffers.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <Table hover size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Nimi</th>
                      <th>Luotu</th>
                      <th>Voimassa</th>
                      <th className="text-end">Kertasumma</th>
                      <th className="text-end">Kuukausittain</th>
                      <th>Tulos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sorted = [...catOffers].sort((a, b) => {
                        const nameA = a.customer?.name ?? '';
                        const nameB = b.customer?.name ?? '';
                        if (nameA !== nameB) return nameA.localeCompare(nameB, 'fi');
                        return (b.originated_at ?? '') > (a.originated_at ?? '') ? 1 : -1;
                      });
                      const rows = [];
                      let lastCustomerId = null;
                      sorted.forEach(o => {
                        if (o.customer_id !== lastCustomerId) {
                          lastCustomerId = o.customer_id;
                          rows.push(
                            <tr key={`cust-${o.customer_id}`} style={{ cursor: 'pointer' }} onClick={() => onCustomerClick(o.customer_id)}>
                              <td colSpan={6} className="fw-bold" style={{ fontSize: '0.95rem', background: '#f8f9fa', borderTop: '2px solid #dee2e6', paddingTop: '0.6rem', color: '#0d6efd' }}>
                                {o.customer?.name ?? `Asiakas #${o.customer_id}`}
                              </td>
                            </tr>
                          );
                        }
                        rows.push(
                          <tr key={o.id} style={{ background: '#fff' }}>
                            <td style={{ paddingLeft: '2rem' }}><strong>{o.name || `#${o.id}`}</strong></td>
                            <td className="text-muted">{o.originated_at?.slice(0, 10) ?? '—'}</td>
                            <td className="text-muted">{o.valid_to ?? '—'}</td>
                            <td className="text-end text-muted">
                              {o.one_time_net_total != null ? `${o.one_time_net_total.toLocaleString('fi-FI')} €` : '—'}
                            </td>
                            <td className="text-end text-muted">
                              {o.monthly_net_total != null && o.monthly_net_total !== 0 ? `${o.monthly_net_total.toLocaleString('fi-FI')} €/kk` : '—'}
                            </td>
                            <td>{offerOutcomeBadge(o.outcome)}</td>
                          </tr>
                        );
                      });
                      return rows;
                    })()}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}
    </TablePane>
  );
}

function AddressSearchPanel({ osoite: initOsoite, paikkakunta: initPaikkakunta, onClose }) {
  const [osoite, setOsoite] = useState(initOsoite);
  const [paikkakunta, setPaikkakunta] = useState(initPaikkakunta);
  const [results, setResults] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState(null);
  const [mapCoords, setMapCoords] = useState(null);

  const doSearch = (addr, city) => {
    setPanelLoading(true);
    setPanelError(null);
    searchKiinteistot('', addr, city)
      .then(res => setResults(res.data || []))
      .catch(e => setPanelError(e.message))
      .finally(() => setPanelLoading(false));
  };

  useEffect(() => { doSearch(initOsoite, initPaikkakunta); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ width: '100%', maxWidth: 460, borderLeft: '1px solid #dee2e6', borderTop: '1px solid #dee2e6', paddingLeft: '1rem', paddingTop: '0.5rem', flexShrink: 0, overflowY: 'auto', maxHeight: 'calc(100vh - 40px)', position: 'sticky', top: 20, alignSelf: 'flex-start' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold small">Kiinteistöhaku</span>
        <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>✕</button>
      </div>
      <div className="d-flex flex-column gap-1 mb-2">
        <input
          className="form-control form-control-sm"
          placeholder="Osoite"
          value={osoite}
          onChange={e => setOsoite(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch(osoite, paikkakunta)}
        />
        <input
          className="form-control form-control-sm"
          placeholder="Paikkakunta"
          value={paikkakunta}
          onChange={e => setPaikkakunta(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch(osoite, paikkakunta)}
        />
        <button className="btn btn-sm btn-outline-success" onClick={() => doSearch(osoite, paikkakunta)} disabled={panelLoading}>
          {panelLoading ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-search me-1" />Hae</>}
        </button>
      </div>
      {panelError && <div className="alert alert-danger small">{panelError}</div>}
      {results !== null && !panelLoading && results.length === 0 && !panelError && (
        <p className="text-muted small">Ei tuloksia</p>
      )}
      {results !== null && !panelLoading && results.length > 0 && (
        <Resultdisplay data={results} setMapCoords={setMapCoords} />
      )}
    </div>
  );
}

export default function Koho() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') ?? 'projects';
  const selectedCustomerId = searchParams.get('customer') ? Number(searchParams.get('customer')) : null;

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchPanel, setSearchPanel] = useState(null);

  const { data: employeesData } = useKoho(getKohoEmployees);
  const employeesById = new Map(employeesData.map(e => [e.id, e.name]));
  const { data: customersData } = useKoho(getKohoCustomers, { active: undefined, limit: 5000 }, null);
  const customersById = new Map(customersData.map(c => [String(c.id), c]));

  const setActiveTab = (tab) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('tab', tab);
    return next;
  });

  const setSelectedCustomerId = (id) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (id != null) next.set('customer', id); else next.delete('customer');
    return next;
  });

  const handleCustomerClick = (customerId, project = null, contract = null) => {
    setSelectedProject(project);
    setSelectedContract(contract);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'customers');
      if (customerId != null) next.set('customer', customerId); else next.delete('customer');
      return next;
    });
  };

  const onAddressSearch = (osoite, paikkakunta) => setSearchPanel({ osoite, paikkakunta });

  const handleCategoryClick = (categoryId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'categories');
      if (categoryId != null) next.set('category', categoryId); else next.delete('category');
      return next;
    });
  };

  const TAB_OPTIONS = [
    { key: 'projects',   label: 'Projektit' },
    { key: 'contracts',  label: 'Sopimukset' },
    { key: 'offers',     label: 'Tarjoukset' },
    { key: 'customers',  label: 'Asiakkaat' },
    { key: 'categories', label: 'Kategoriat' },
    { key: 'invoices',   label: 'Laskut' },
    { key: 'employees',  label: 'Työntekijät' },
  ];

  return (
    <div className="main-content px-2 px-md-4 py-3 py-md-4" style={{ maxWidth: searchPanel ? 'none' : 1400 }}>
      <style>{`
        .koho-money {
          color: #198754;
          font-weight: 600;
        }
        .koho-section-card {
          border-radius: 0.5rem !important;
          background: #fff;
          padding: 1rem;
          border: 1px solid #dee2e6;
          border-left-width: 4px !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          transition: box-shadow 0.15s ease, transform 0.15s ease;
        }
        .koho-section-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateY(-1px);
        }
        .koho-section-card.card-projects   { border-left-color: #198754; }
        .koho-section-card.card-contracts  { border-left-color: #0d6efd; }
        .koho-section-card.card-offers     { border-left-color: #0dcaf0; }
        .koho-section-card.card-invoices   { border-left-color: #ffc107; }
        .koho-section-card.card-billing    { border-left-color: #6c757d; }
        @media (max-width: 767px) {
          .koho-table thead { display: none; }
          .koho-table tbody tr {
            display: block;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            margin-bottom: 0.6rem;
            padding: 0.5rem 0.75rem;
            background: #fff;
          }
          .koho-table tbody td {
            display: block;
            border: none !important;
            padding: 0.15rem 0;
            font-size: 0.85rem;
          }
          .koho-table tbody td:first-child {
            font-size: 0.95rem;
            font-weight: 600;
            padding-bottom: 0.3rem;
            border-bottom: 1px solid #f0f0f0 !important;
            margin-bottom: 0.2rem;
          }
          .koho-table tbody td:empty { display: none; }
          .koho-table-wrap { overflow-x: unset !important; }
        }
      `}</style>
      <h4 style={{ fontFamily: "'Josefin Sans', sans-serif", color: BRAND, marginBottom: '1.5rem' }}>
        Koho ERP
      </h4>
      <div className="d-flex flex-column flex-lg-row gap-3 align-items-start">
        <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
          {/* Mobile: select dropdown */}
          <select
            className="form-select mb-3 d-md-none"
            value={activeTab}
            onChange={e => setActiveTab(e.target.value)}
          >
            {TAB_OPTIONS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>

          {/* Desktop: tab nav */}
          <ul className="nav nav-tabs mb-3 d-none d-md-flex">
            {TAB_OPTIONS.map(t => (
              <li key={t.key} className="nav-item">
                <button
                  className={`nav-link${activeTab === t.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>

          {activeTab === 'projects'  && <ProjectsTab onCustomerClick={handleCustomerClick} employeesById={employeesById} customersById={customersById} onAddressSearch={onAddressSearch} />}
          {activeTab === 'customers' && <CustomersTab selectedCustomerId={selectedCustomerId} selectedProject={selectedProject} selectedContract={selectedContract} onSelectCustomer={(id) => { setSelectedCustomerId(id); setSelectedProject(null); setSelectedContract(null); }} onClearSelection={() => { setSelectedCustomerId(null); setSelectedProject(null); setSelectedContract(null); }} employeesById={employeesById} onAddressSearch={onAddressSearch} onCategoryClick={handleCategoryClick} />}
          {activeTab === 'categories' && <CategoriesTab customersData={customersData} onCustomerClick={handleCustomerClick} employeesById={employeesById} />}
          {activeTab === 'invoices'  && <InvoicesTab />}
          {activeTab === 'employees' && <EmployeesTab onCustomerClick={handleCustomerClick} />}
          {activeTab === 'offers'    && <OffersTab onCustomerClick={handleCustomerClick} employeesById={employeesById} />}
          {activeTab === 'contracts' && <ContractsTab onCustomerClick={handleCustomerClick} employeesById={employeesById} customersById={customersById} />}
        </div>
        {searchPanel && (
          <AddressSearchPanel
            key={searchPanel.osoite + '|' + searchPanel.paikkakunta}
            osoite={searchPanel.osoite}
            paikkakunta={searchPanel.paikkakunta}
            onClose={() => setSearchPanel(null)}
          />
        )}
      </div>
    </div>
  );
}
